import { ComplexityEnum } from "@nlc/shared-types";

import type { BadgeVariant } from "@/components/badge";

/**
 * Complexity option definition
 */
export interface ComplexityOption {
  id: number;
  name: string;
  stringValue: ComplexityEnum;
  variant: BadgeVariant;
}

/**
 * Unified complexity options with consistent mapping
 * Maps database IDs to display options with star ratings
 */
export const COMPLEXITY_OPTIONS: ComplexityOption[] = [
  {
    id: 1,
    name: "Basic",
    stringValue: ComplexityEnum.BASIC,
    variant: "success",
  },
  {
    id: 2,
    name: "Average",
    stringValue: ComplexityEnum.AVERAGE,
    variant: "warning",
  },
  {
    id: 3,
    name: "Complex",
    stringValue: ComplexityEnum.COMPLEX,
    variant: "error",
  },
] as const;

/**
 * Mapping from database ID to complexity option
 */
export const COMPLEXITY_ID_MAP = Object.fromEntries(
  COMPLEXITY_OPTIONS.map((option) => [option.id, option]),
) as Record<number, ComplexityOption>;

/**
 * Mapping from string value to complexity option
 */
export const COMPLEXITY_STRING_MAP = Object.fromEntries(
  COMPLEXITY_OPTIONS.map((option) => [option.stringValue, option]),
) as Record<ComplexityEnum, ComplexityOption>;

/**
 * Get complexity option by database ID
 */
export const getComplexityById = (id: number): ComplexityOption | undefined =>
  COMPLEXITY_ID_MAP[id];

/**
 * Get complexity option by string value
 */
export const getComplexityByString = (
  value: ComplexityEnum,
): ComplexityOption | undefined => COMPLEXITY_STRING_MAP[value];

/**
 * Render complexity as stars (★)
 */
export const renderComplexityStars = (stars: number): string =>
  "★".repeat(stars);

/**
 * Get the badge variant color for a given complexity
 */
export const getComplexityColor = (
  complexity: ComplexityEnum | number,
): BadgeVariant => {
  const option =
    typeof complexity === "number"
      ? getComplexityById(complexity)
      : getComplexityByString(complexity);

  return option?.variant ?? "primary";
};
